import { useEffect, useState } from "react";
import { CreditCard, Ticket, CheckCircle, Lock, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin test mode: ?kartentest=1 while logged into the admin dashboard
export const cardHeaders = (extra = {}) => {
  const h = { "Content-Type": "application/json", ...extra };
  const admin = localStorage.getItem("adminToken");
  if (admin && new URLSearchParams(window.location.search).get("kartentest") === "1") h["X-Admin-Token"] = admin;
  return h;
};

export const useCardsAccess = () => {
  const [state, setState] = useState({ enabled: false, isPublic: false, testMode: false });
  useEffect(() => {
    fetch(`${API_URL}/api/cards/config`).then((r) => r.json()).then((cfg) => {
      const testMode = !cfg.public_enabled && !!cardHeaders()["X-Admin-Token"];
      setState({ enabled: cfg.public_enabled || testMode, isPublic: cfg.public_enabled, testMode });
    }).catch(() => {});
  }, []);
  return state;
};

export const cardTitle = (card) => `${card.package_name} – ${card.duration} Minuten`;
export const cardBalance = (card) => `Noch ${card.credits_remaining} von ${card.credits_total} Trainingseinheiten verfügbar`;

export const PayModeSwitch = ({ value, onChange, showCard = true }) => (
  <div className={`grid grid-cols-1 ${showCard ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`} data-testid="pay-mode-switch">
    {[
      { id: "regular", label: "Regulär buchen und bezahlen", sub: "PayPal, Überweisung oder bar", Icon: CreditCard },
      ...(showCard ? [{ id: "card", label: "Mit Trainingskarte buchen", sub: "5er- oder 10er-Karte einlösen", Icon: Ticket }] : []),
      { id: "abo", label: "Mit aktivem Monatsabo buchen", sub: "Ohne erneute Zahlung", Icon: Repeat },
    ].map(({ id, label, sub, Icon }) => (
      <button key={id} type="button" onClick={() => onChange(id)} data-testid={`pay-mode-${id}`}
        className={`p-4 rounded-lg border text-left transition-colors ${value === id ? "bg-[#c1ff72]/10 border-[#c1ff72]" : "bg-[#1A1A1A] border-white/10 hover:border-white/30"}`}>
        <div className="flex items-center gap-2 font-semibold"><Icon className="w-4 h-4 text-[#c1ff72]" />{label}</div>
        <p className="text-xs text-[#71717A] mt-1">{sub}</p>
      </button>
    ))}
  </div>
);

export const CardVerifyBox = ({ onVerified }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/cards/verify`, {
        method: "POST", headers: cardHeaders(), body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json();
      if (res.ok) onVerified(data.card, code.trim(), email.trim());
      else setError(typeof data.detail === "string" ? data.detail : "Bitte E-Mail und Kartencode prüfen.");
    } catch {
      setError("Ein Fehler ist aufgetreten");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={verify} className="bg-[#1A1A1A] p-4 sm:p-5 rounded-lg border border-white/10 space-y-4" data-testid="card-verify-box">
      <div>
        <Label htmlFor="card-email" className="text-white">E-Mail-Adresse der Karte</Label>
        <Input id="card-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="max@beispiel.de" className="mt-1 bg-[#0A0A0A] border-white/20" data-testid="input-card-email" />
      </div>
      <div>
        <Label htmlFor="card-code" className="text-white">Persönlicher Kartencode</Label>
        <Input id="card-code" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="off"
          placeholder="XXXX-XXXX-XXXX" className="mt-1 bg-[#0A0A0A] border-white/20 font-mono tracking-widest" data-testid="input-card-code" />
      </div>
      {error && <p className="text-sm text-red-400" data-testid="card-verify-error">{error}</p>}
      <Button type="submit" disabled={loading || !email || !code} className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold" data-testid="btn-card-verify">
        <Lock className="w-4 h-4 mr-2" />{loading ? "Wird geprüft..." : "Karte prüfen"}
      </Button>
      <p className="text-xs text-[#71717A]">Den Code hast du nach Zahlungseingang per E-Mail erhalten. Trainingskarten gelten für 60-Minuten-Training.</p>
    </form>
  );
};

export const CardSummary = ({ card, onReset }) => (
  <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/40 rounded-lg p-4 sm:p-5" data-testid="card-summary">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="flex items-center gap-2 font-semibold text-[#c1ff72]"><CheckCircle className="w-4 h-4" />{cardTitle(card)}</p>
        <p className="text-sm text-white mt-1" data-testid="card-summary-training">{card.training_name}</p>
        <p className="text-sm text-[#A1A1AA] mt-1" data-testid="card-summary-balance">{cardBalance(card)}</p>
      </div>
      {onReset && (
        <button type="button" onClick={onReset} className="text-xs text-[#71717A] hover:text-white underline shrink-0" data-testid="btn-card-reset">
          Andere Karte
        </button>
      )}
    </div>
    <p className="text-xs text-[#71717A] mt-3">Trainingsart und Dauer (60 Minuten) sind durch deine Karte festgelegt.</p>
  </div>
);
